
import React from 'react';
import { MainLink, Link, ThemeSettings } from '@/types/link-short';
import { LinkCard } from './LinkCard';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface ProfilePreviewProps {
  mainLink: MainLink;
  links: Link[];
  userProfile?: {
    name?: string;
    avatar_url?: string;
    agency_name?: string;
  };
  isPreview?: boolean;
  onLinkClick?: (link: Link) => void;
  onEditLink?: (link: Link) => void;
  onDeleteLink?: (linkId: string) => void;
}

export function ProfilePreview({
  mainLink,
  links,
  userProfile,
  isPreview = false,
  onLinkClick,
  onEditLink,
  onDeleteLink
}: ProfilePreviewProps) {
  const themeSettings = (mainLink.theme_settings as ThemeSettings) || {};
  
  const backgroundStyle = React.useMemo(() => {
    const settings = themeSettings as ThemeSettings;
    
    switch (settings?.backgroundType) {
      case 'gradient':
        return {
          background: settings?.backgroundGradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        };
      case 'image':
        return {
          backgroundImage: settings?.backgroundImageUrl ? `url(${settings.backgroundImageUrl})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        };
      default:
        return {
          backgroundColor: settings?.backgroundColor || mainLink.bg_color || '#f8fafc'
        };
    }
  }, [themeSettings, mainLink.bg_color]);

  const textColor = (themeSettings as ThemeSettings)?.textColor || mainLink.text_color || '#1e293b';
  const layout = (themeSettings as ThemeSettings)?.layout || 'default';
  const avatarStyle = (themeSettings as ThemeSettings)?.avatarStyle || 'circle';
  const showBio = (themeSettings as ThemeSettings)?.showBio !== false;

  const containerClass = {
    default: 'max-w-md mx-auto',
    compact: 'max-w-sm mx-auto',
    wide: 'max-w-lg mx-auto',
    minimal: 'max-w-xs mx-auto'
  }[layout];

  const avatarClass = {
    circle: 'rounded-full',
    square: 'rounded-none',
    rounded: 'rounded-xl'
  }[avatarStyle];

  // Use avatar from mainLink or userProfile
  const avatarUrl = mainLink.avatar_url || userProfile?.avatar_url;

  return (
    <div 
      className="h-full w-full py-8 px-4 flex flex-col"
      style={backgroundStyle}
    >
      <div className={`${containerClass} w-full flex-1`}>
        {/* Profile Header */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="relative inline-block mb-6">
            <Avatar className={`h-28 w-28 mx-auto border-4 border-white shadow-xl ${avatarClass}`}>
              <AvatarImage src={avatarUrl} alt={mainLink.title} />
              <AvatarFallback 
                className="text-3xl font-bold bg-gradient-to-br from-blue-500 to-purple-600 text-white"
              >
                {userProfile?.name?.charAt(0) || userProfile?.agency_name?.charAt(0) || mainLink.title?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-gradient-to-r from-green-400 to-green-500 border-3 border-white rounded-full shadow-lg"></div>
          </div>
          
          <h1 
            className="text-3xl font-bold mb-3 tracking-tight"
            style={{ color: textColor }}
          >
            {mainLink.title}
          </h1>
          
          {mainLink.description && showBio && (
            <p 
              className="text-base leading-relaxed mb-3 max-w-sm mx-auto"
              style={{ color: textColor, opacity: 0.85 }}
            >
              {mainLink.description}
            </p>
          )}
          
          {userProfile?.agency_name && (
            <p 
              className="text-sm font-medium px-3 py-1 bg-white/20 rounded-full inline-block"
              style={{ color: textColor, opacity: 0.7 }}
            >
              @{userProfile.agency_name.toLowerCase().replace(/\s+/g, '')}
            </p>
          )}
        </div>

        {/* Links Container */}
        <div className="space-y-4 mb-8">
          {links
            .filter(link => link.is_active)
            .sort((a, b) => a.order_index - b.order_index)
            .map((link, index) => (
              <div
                key={link.id}
                className="animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <LinkCard
                  link={link}
                  isPreview={isPreview}
                  onClick={onLinkClick}
                  onEdit={onEditLink}
                  onDelete={onDeleteLink}
                  themeSettings={themeSettings}
                />
              </div>
            ))}
        </div>

        {/* Empty State */}
        {links.filter(link => link.is_active).length === 0 && (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto mb-6 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
              <svg className="w-10 h-10" style={{ color: textColor, opacity: 0.6 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <p style={{ color: textColor, opacity: 0.7 }} className="text-lg font-medium">
              Nenhum link disponível
            </p>
            <p style={{ color: textColor, opacity: 0.5 }} className="text-sm mt-2">
              Adicione seus primeiros links para começar
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
